import { request, type BizResponse } from '@/utils/request'
import { API_BASE_URL } from '@/config/env'
import { BizCode } from '@/config/enums'
import { getToken } from '@/utils/auth'
import { getDeviceId } from '@/utils/device'
import type {
  ChangePasswordReq,
  LoginByPasswordReq,
  LoginReq,
  LoginRes,
  RefreshTokenReq,
  RegisterReq,
  SendSmsReq,
  UpdateProfileReq,
  UserInfo,
} from '@/api/types/user'

const PREFIX = '/api/v1/user'

export function login(data: LoginReq): Promise<LoginRes> {
  return request<LoginRes>({
    url: `${PREFIX}/login`,
    method: 'POST',
    data,
    withAuth: false,
  })
}

export function loginByPassword(data: LoginByPasswordReq): Promise<LoginRes> {
  return request<LoginRes>({
    url: `${PREFIX}/login-password`,
    method: 'POST',
    data,
    withAuth: false,
  })
}

export function sendSmsCode(data: SendSmsReq): Promise<void> {
  return request<void>({
    url: `${PREFIX}/sms`,
    method: 'POST',
    data,
    withAuth: false,
  })
}

export function getUserInfo(options?: { silent?: boolean }): Promise<UserInfo> {
  return request<UserInfo>({
    url: `${PREFIX}/info`,
    method: 'GET',
    silent: options?.silent,
  })
}

export function logout(): Promise<void> {
  return request<void>({
    url: `${PREFIX}/logout`,
    method: 'POST',
  })
}

export function refreshToken(data: RefreshTokenReq): Promise<LoginRes> {
  return request<LoginRes>({
    url: `${PREFIX}/refresh`,
    method: 'POST',
    data,
    withAuth: false,
    silent: true,
  })
}

export function register(data: RegisterReq): Promise<LoginRes> {
  return request<LoginRes>({
    url: `${PREFIX}/register`,
    method: 'POST',
    data,
    withAuth: false,
  })
}

export function updateProfile(data: UpdateProfileReq): Promise<UserInfo> {
  return request<UserInfo>({
    url: `${PREFIX}/profile`,
    // 语义为 PATCH；uni.request 各端不保证支持 PATCH，统一用 PUT
    method: 'PUT',
    data,
  })
}

export function changePassword(data: ChangePasswordReq): Promise<void> {
  return request<void>({
    url: `${PREFIX}/change-password`,
    method: 'POST',
    data,
  })
}

/** 头像上传：multipart 走 uni.uploadFile，不经过 request 封装 */
export function uploadAvatar(filePath: string): Promise<UserInfo> {
  return new Promise((resolve, reject) => {
    uni.uploadFile({
      url: `${API_BASE_URL}${PREFIX}/avatar`,
      filePath,
      name: 'file',
      header: {
        Authorization: `Bearer ${getToken()}`,
        'X-Device-Id': getDeviceId(),
      },
      success: (res) => {
        try {
          const body = JSON.parse(res.data) as BizResponse<UserInfo>
          if (body.code === BizCode.SUCCESS) {
            resolve(body.data)
          } else {
            reject(body)
          }
        } catch {
          reject(new Error(`HTTP ${res.statusCode}`))
        }
      },
      fail: (err) => reject(err),
    })
  })
}
